import * as Sentry from "@sentry/react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { isLocal } from "@/config";
import useAuth, { type AuthState } from "@/store/auth.slice";
import { isDatadogInitialized, loadDatadog } from "./datadog";
import { initializeAnalytics } from "./initializeAnalytics";

export const AnalyticsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [sessionStartTime] = useState(new Date().toISOString());
  const [pageHiddenTime, setPageHiddenTime] = useState<number | null>(null);
  const [hasLoggedLoginEvent, setHasLoggedLoginEvent] = useState(false);
  const [isDatadogReady, setIsDatadogReady] = useState(false);
  const syncInProgress = useRef(false);
  const lastSyncedUser = useRef<string | null>(null);

  // Initialize analytics services
  useEffect(() => {
    if (!isLocal) {
      const unsubscribe = initializeAnalytics();

      // Wait for Datadog to be ready
      const checkDatadogReady = () => {
        if (isDatadogInitialized()) {
          setIsDatadogReady(true);
        } else {
          setTimeout(checkDatadogReady, 100);
        }
      };
      setTimeout(checkDatadogReady, 500);

      return () => unsubscribe();
    }
  }, []);

  // Improved user data sync with better error handling and deduplication
  const syncUserData = useCallback(
    async (state: AuthState, isInitialSync = false) => {
      // Prevent concurrent syncs
      if (syncInProgress.current) return;
      syncInProgress.current = true;

      try {
        const { loggedIn, user, isEmployee } = state;
        const userId = user?.id?.toString() || null;

        // Skip if user hasn't changed (prevents unnecessary API calls)
        if (!isInitialSync && lastSyncedUser.current === userId) {
          return;
        }

        const userData = {
          id: userId || undefined,
          email: user?.email || undefined,
          company: user?.default_tenant?.slug || undefined,
          loggedIn,
          employee: isEmployee,
        };

        // Update Sentry
        Sentry.setUser(loggedIn ? userData : null);

        // Update Datadog
        const datadogRum = await loadDatadog();

        if (loggedIn && user && userId) {
          // Enhanced user information
          datadogRum.setUser({
            id: userId,
            email: user.email || "unknown",
            name: user.name || user.email || "unknown",
            company: user.default_tenant?.slug || "unknown",
            isEmployee: isEmployee,
            accountType: isEmployee ? "employee" : "customer",
          });

          // Enhanced global context
          datadogRum.setGlobalContext({
            user: {
              company: user.default_tenant?.slug || "unknown",
              isEmployee: isEmployee,
              accountId: user.default_tenant?.id?.toString() || "unknown",
            },
            session: {
              startTime: sessionStartTime,
              userRole: isEmployee ? "employee" : "user",
              tenant: user.default_tenant?.slug || "unknown",
              environment: import.meta.env.VITE_ENVIRONMENT || "development",
            },
          });

          // Track login event (only once per session)
          if (isInitialSync && !hasLoggedLoginEvent) {
            datadogRum.addAction("user_login", {
              userId: userId,
              email: user.email,
              company: user.default_tenant?.slug,
              isEmployee,
              timestamp: new Date().toISOString(),
              sessionId: sessionStartTime,
            });
            setHasLoggedLoginEvent(true);
          }

          lastSyncedUser.current = userId;
        } else if (!loggedIn) {
          // Clear user information when logged out
          datadogRum.setUser({});

          // Keep session info, don't clear everything
          datadogRum.setGlobalContext({
            session: {
              startTime: sessionStartTime,
              environment: import.meta.env.VITE_ENVIRONMENT || "development",
            },
          });

          // Track logout event
          if (hasLoggedLoginEvent) {
            datadogRum.addAction("user_logout", {
              timestamp: new Date().toISOString(),
              previousUserId: lastSyncedUser.current,
            });
            setHasLoggedLoginEvent(false);
          }

          lastSyncedUser.current = null;
        }
      } catch (error) {
        console.error("Error syncing user data to analytics:", error);
      } finally {
        syncInProgress.current = false;
      }
    },
    [sessionStartTime, hasLoggedLoginEvent],
  );

  // Set up user tracking with better timing
  useEffect(() => {
    if (!isDatadogReady) return;

    // Initial sync with delay to ensure auth is ready
    const timeoutId = setTimeout(() => {
      const currentState = useAuth.getState();
      syncUserData(currentState, true);
    }, 100);

    // Subscribe to auth changes with small delay for batching
    const unsubscribe = useAuth.subscribe((state) => {
      setTimeout(() => syncUserData(state, false), 50);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [isDatadogReady, syncUserData]);

  // Handle visibility change with improved session tracking
  useEffect(() => {
    const handleVisibilityChange = async () => {
      try {
        const currentState = useAuth.getState();

        if (document.visibilityState === "hidden") {
          setPageHiddenTime(Date.now());

          // Track session pause
          const datadogRum = await loadDatadog();
          if (currentState.loggedIn) {
            datadogRum.addAction("session_pause", {
              timestamp: new Date().toISOString(),
              userId: currentState.user?.id?.toString(),
            });
          }
        } else if (document.visibilityState === "visible" && pageHiddenTime) {
          const idleTime = Date.now() - pageHiddenTime;

          // Re-sync user data when tab becomes visible
          await syncUserData(currentState, false);

          // Track session resume with idle time
          const datadogRum = await loadDatadog();
          if (currentState.loggedIn) {
            datadogRum.addAction("session_resume", {
              timestamp: new Date().toISOString(),
              userId: currentState.user?.id?.toString(),
              idleTime,
              wasIdle: idleTime > 30000, // Flag if idle for more than 30 seconds
            });
          }

          setPageHiddenTime(null);
        }
      } catch (error) {
        console.error("Error handling visibility change:", error);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [pageHiddenTime, syncUserData]);

  // Add beforeunload handler to track session end
  useEffect(() => {
    const handleBeforeUnload = async () => {
      try {
        const currentState = useAuth.getState();
        if (currentState.loggedIn) {
          const datadogRum = await loadDatadog();
          datadogRum.addAction("session_end", {
            timestamp: new Date().toISOString(),
            userId: currentState.user?.id?.toString(),
            sessionDuration: Date.now() - new Date(sessionStartTime).getTime(),
          });
        }
      } catch (_error) {
        // Ignore errors during unload
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [sessionStartTime]);

  return <>{children}</>;
};
